"use client";

import React, { useEffect } from "react";
import { AlertCircle, CheckCircle2, Minus, Plus, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Addon } from "@/domain/entities/addon.entity";
import { SelectedAddon } from "@/domain/entities/booking.entity";
import { AsyncStatus, ASYNC_STATUS } from "@/hooks/use-booking-flow";
import { cn } from "@/lib/utils";
import { formatMYR } from "@/shared/utils/currency";

interface AddonsStepProps {
  addons: Addon[];
  isLoading: boolean;
  status: AsyncStatus;
  selectedAddons: SelectedAddon[];
  selectedAddonQuantities: Record<string, number>;
  onFetchAddons: () => void | Promise<void>;
  onUpdateAddon: (addonId: string, quantity: number) => void;
}

export const AddonsStep: React.FC<AddonsStepProps> = ({
  addons,
  isLoading,
  status,
  selectedAddonQuantities,
  onFetchAddons,
  onUpdateAddon,
}) => {
  useEffect(() => {
    if (status !== ASYNC_STATUS.INITIAL) {
      return;
    }

    void onFetchAddons();
  }, [onFetchAddons, status]);

  const renderContent = () => {
    if (status === ASYNC_STATUS.INITIAL || isLoading) {
      return (
        <div className="flex min-h-[420px] flex-col items-center justify-center gap-6">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
          <p className="animate-pulse text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Gathering sparkles
          </p>
        </div>
      );
    }

    if (status === ASYNC_STATUS.FAILURE) {
      return (
        <div className="flex min-h-[420px] flex-col items-center justify-center">
          <div className="flex min-h-[320px] w-full max-w-4xl flex-col items-center justify-center gap-5 rounded-[2.5rem] border border-red-500/20 bg-red-500/[0.05] px-6 py-12 text-center shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-md">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-red-500/20 bg-red-500/10 text-red-300">
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-200">
                Unable to load add-ons
              </p>
              <p className="max-w-md text-sm leading-6 text-red-100/80">
                The add-on list could not be loaded right now. Please try again.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => void onFetchAddons()}
              className="rounded-full border-red-500/30 bg-transparent text-red-100 hover:bg-red-500/10 hover:text-red-50"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    if (addons.length === 0) {
      return (
        <div className="flex min-h-[420px] flex-col items-center justify-center">
          <div className="flex min-h-[320px] w-full max-w-4xl flex-col items-center justify-center gap-5 rounded-[2.5rem] border border-white/12 bg-white/[0.06] px-6 py-12 text-center shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-md">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary">
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-foreground">
                No add-ons available
              </p>
              <p className="max-w-md text-sm leading-6 text-muted-foreground/80">
                There are no active add-ons to show at the moment. Please check back later.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {addons.map((addon) => {
          const quantity = selectedAddonQuantities[addon.id] || 0;
          const isSelected = quantity > 0;

          return (
            <div
              key={addon.id}
              className={cn(
                "group flex items-center justify-between rounded-[1.5rem] border p-6 transition-all duration-300",
                isSelected
                  ? "border-primary bg-primary/2 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.02)]"
                  : "border-border bg-card hover:border-primary/30"
              )}
            >
              <div className="mr-6 flex-1">
                <div className="mb-1.5 flex items-center gap-2">
                  <h3 className="text-base font-serif text-foreground transition-colors group-hover:text-primary">
                    {addon.name}
                  </h3>
                  {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                </div>
                {addon.description && (
                  <p className="line-clamp-1 text-xs font-light leading-relaxed text-muted-foreground/80">
                    {addon.description}
                  </p>
                )}
                <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-primary">
                  {formatMYR(addon.price)} {addon.unit ? `/ ${addon.unit}` : ""}
                </div>
              </div>

              <div className="flex items-center gap-1 rounded-full border border-border/50 bg-muted/50 p-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full transition-all hover:bg-card hover:text-primary disabled:opacity-30"
                  onClick={() => onUpdateAddon(addon.id, quantity - 1)}
                  disabled={quantity === 0}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                <span className="w-8 text-center text-xs font-bold tabular-nums text-foreground">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full transition-all hover:bg-card hover:text-primary"
                  onClick={() => onUpdateAddon(addon.id, quantity + 1)}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="min-h-[420px]">{renderContent()}</div>
    </div>
  );
};
